/**
 * Shared overlay shell (STN client round 2026-08) — the one recipe for every
 * modal surface (ui-best-practices §1 "one shared recipe per visual concept"):
 * the right-side drawers (experience, saved searches, weighting) and, via
 * variant="modal", centered dialogs (contact modal). The selector stays
 * `maps-drawer` even though it also renders modals — renaming would churn
 * three shipped consumers for cosmetics; this comment is the map.
 *
 * Built on native <dialog>.showModal() (ui-best-practices §4): top layer above
 * every stacking context, real focus trap with browser-managed focus restore,
 * inert background, layered Escape (the browser closes only the top-most
 * modal per press), and a ::backdrop scrim — none of which the app's
 * hand-rolled popover pattern provides.
 *
 * Usage: the parent *ngIf-mounts the drawer; the dialog opens on init and the
 * parent unmounts it on (closed). Header content projects into [drawerTitle],
 * footer actions into [drawerFooter]; everything else is the scrollable body.
 */
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';

@Component({
    selector: 'maps-drawer',
    standalone: false,
    templateUrl: './drawer.component.html',
    styleUrl: './drawer.component.scss',
})
export class DrawerComponent implements AfterViewInit, OnDestroy {
    /** Accessible name for the dialog. */
    @Input({ required: true }) label!: string;
    /** Right-side drawer (default) or centered modal (contact modal). Both
     *  share the SAME close mechanics below — that logic must never fork. */
    @Input() variant: 'drawer' | 'modal' = 'drawer';
    /** Navy gradient header (experience drawer) vs plain white. */
    @Input() headerStyle: 'plain' | 'navy' = 'plain';
    /** Wider variant (weighting drawer's 540px vs the default 520px). */
    @Input() wide = false;
    /** Fires after the native dialog closes (X, Escape, or backdrop click). */
    @Output() closed = new EventEmitter<void>();
    /**
     * Optional close veto, consulted by EVERY close route (X, Escape,
     * backdrop, programmatic Close()) — the single-funnel design means one
     * hook guards every exit path (ui-best-practices §7). Return false to keep
     * the dialog open; the guard owner then shows its own INLINE confirm — a
     * body-appended dialog would sit beneath the native top layer
     * (ui-best-practices §6). Bind an arrow function so `this` survives.
     * ngOnDestroy still closes unconditionally (unmount is not a user cancel).
     */
    @Input() closeGuard: (() => boolean) | null = null;

    @ViewChild('dialog', { static: true }) dialogRef!: ElementRef<HTMLDialogElement>;

    constructor(private readonly zone: NgZone) {}

    ngAfterViewInit(): void {
        this.dialogRef.nativeElement.showModal();
    }

    ngOnDestroy(): void {
        const dialog = this.dialogRef.nativeElement;
        if (dialog.open) dialog.close();
    }

    /**
     * Single close path. The native dialog 'close' event is deliberately NOT
     * relied on: live Phase E verification showed it never reaching listeners
     * in this zone.js setup (neither Angular template bindings nor direct
     * addEventListener fired), which left a closed-but-mounted drawer whose
     * *ngIf never tore down and which could never reopen. Instead every close
     * route — the X button, backdrop click, and Escape (intercepted below
     * before the native cancel) — funnels here, closing the dialog AND
     * emitting inside NgZone.run so change detection tears the drawer down.
     */
    Close(force = false): void {
        const dialog = this.dialogRef.nativeElement;
        if (!dialog.open) return; // already closing — never double-emit
        if (!force && this.closeGuard !== null && !this.closeGuard()) return; // vetoed — guard owner shows its inline confirm
        dialog.close();
        this.zone.run(() => this.closed.emit());
    }

    /** Escape: preempt the browser's cancel-close (whose 'close' event we'd
     *  never see — above) and run the explicit close path instead. Inner
     *  surfaces that own their Escape (e.g. an inline edit) stopPropagation
     *  before this fires, preserving layered Escape. */
    OnEscape(event: Event): void {
        event.preventDefault();
        this.Close();
    }

    /** Backdrop click = click landing on the <dialog> element itself (the
     *  frame fills it, so content clicks never match). */
    OnBackdropClick(event: MouseEvent): void {
        if (event.target === this.dialogRef.nativeElement) this.Close();
    }
}
